/**
 * Geometry for the bottom-right-anchored, resizable Scout panel.
 *
 * Anchored by `right`/`bottom` rather than `left`/`top` because the panel
 * lives against the bottom-right corner: anchoring there means the resize grip
 * (top-left) grows the panel without also having to move it, and a browser
 * resize keeps it visually put.
 *
 * Position is fixed — the panel is resizable, not draggable. `right`/`bottom`
 * are still modelled so the clamp can keep the panel inside a window smaller
 * than the size the user chose.
 *
 * Pure functions, no React: the clamping is the part worth testing, since it
 * decides whether a panel can end up unusable on a small window.
 */

export interface PanelFrame {
  /** Distance from the viewport's right edge, px. */
  right: number;
  /** Distance from the viewport's bottom edge, px. */
  bottom: number;
  width: number;
  height: number;
}

export const PANEL_MIN_WIDTH = 320;
export const PANEL_MIN_HEIGHT = 320;
/** Keep this much of the panel on screen in each axis, so it is always grabbable. */
const KEEP_VISIBLE = 80;

export const DEFAULT_PANEL_FRAME: PanelFrame = {
  right: 20,
  bottom: 20,
  width: 380,
  height: 600,
};

export interface Viewport {
  width: number;
  height: number;
}

/**
 * Constrain a frame to the viewport.
 *
 * Size is capped first, then position, because a panel taller than the window
 * would otherwise be pushed off the top by a position clamp that assumed it
 * fitted. Position allows a little negative offset — dragging slightly past
 * the edge is normal — but never enough to put the whole panel out of reach:
 * a panel you cannot grab is a panel the user has lost, and there is no
 * "reset position" affordance.
 */
export function clampFrame(frame: PanelFrame, viewport: Viewport): PanelFrame {
  const width = Math.max(
    PANEL_MIN_WIDTH,
    Math.min(frame.width, Math.max(PANEL_MIN_WIDTH, viewport.width - 16)),
  );
  const height = Math.max(
    PANEL_MIN_HEIGHT,
    Math.min(frame.height, Math.max(PANEL_MIN_HEIGHT, viewport.height - 16)),
  );
  // Largest offset that still leaves KEEP_VISIBLE px on screen.
  const maxRight = Math.max(0, viewport.width - KEEP_VISIBLE);
  const maxBottom = Math.max(0, viewport.height - KEEP_VISIBLE);
  return {
    width,
    height,
    right: Math.min(Math.max(frame.right, -(width - KEEP_VISIBLE)), maxRight),
    bottom: Math.min(Math.max(frame.bottom, -(height - KEEP_VISIBLE)), maxBottom),
  };
}

/**
 * Apply a resize delta from the TOP-LEFT grip. Dragging left/up (negative
 * dx/dy) grows the panel, and because the panel is pinned bottom-right the
 * position needs no adjustment — the far corner stays where it is.
 */
export function resizeFrame(
  frame: PanelFrame,
  dx: number,
  dy: number,
  viewport: Viewport,
): PanelFrame {
  return clampFrame(
    { ...frame, width: frame.width - dx, height: frame.height - dy },
    viewport,
  );
}

const STORAGE_KEY = "proclaw.scout.frame";

/** Read the persisted frame, clamped to the CURRENT viewport. */
export function loadPanelFrame(viewport: Viewport): PanelFrame {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return clampFrame(DEFAULT_PANEL_FRAME, viewport);
    const parsed = JSON.parse(raw) as Partial<PanelFrame>;
    const nums = (["right", "bottom", "width", "height"] as const).every(
      (k) => typeof parsed[k] === "number" && Number.isFinite(parsed[k]),
    );
    if (!nums) return clampFrame(DEFAULT_PANEL_FRAME, viewport);
    // Clamped on read as well as write: the window may be smaller than it was
    // when the frame was saved, or on a different monitor entirely.
    return clampFrame(parsed as PanelFrame, viewport);
  } catch {
    // Private mode / disabled storage — fall back rather than break the widget.
    return clampFrame(DEFAULT_PANEL_FRAME, viewport);
  }
}

export function savePanelFrame(frame: PanelFrame): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(frame));
  } catch {
    // Not worth surfacing: the panel still works, it just won't be remembered.
  }
}
